import { useState } from "react";
import { PaginationBar } from "@/components/ui/PaginationBar";
import type { Requisition } from "../../services/requisitionApi";
import { RequisitionStatus, getStatusLabel } from "../../services/requisitionApi";
import { RequisitionApprovalDialog } from "./RequisitionApprovalDialog";
import { IssueRequisitionDialog } from "./IssueRequisitionDialog";
import { RequisitionReturnDialog } from "./RequisitionReturnDialog";
import {
  getRequisitionItemDescription,
  getRequisitionItemName,
  getRequisitionItemOwner,
  getRequisitionItemReference,
  getRequisitionItemSource,
  getRequisitionItemType,
  isCustomerSeedItem,
} from "./requisitionDisplay";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Eye, CheckCircle2, PackageCheck, Undo2 } from "lucide-react";
import { useAuthStore } from "@/features/auth/stores/authStore";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface RequisitionsTableProps {
  requisitions: Requisition[];
  onRequisitionUpdated?: () => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  isLoading?: boolean;
  error?: unknown;
}

const STATUS_COLORS: Record<number, string> = {
  [RequisitionStatus.Draft]: "outline",
  [RequisitionStatus.PendingApproval]: "outline",
  [RequisitionStatus.Approved]: "secondary",
  [RequisitionStatus.PartiallyIssued]: "secondary",
  [RequisitionStatus.Issued]: "default",
  [RequisitionStatus.Rejected]: "destructive",
  [RequisitionStatus.Cancelled]: "destructive",
};

export const RequisitionsTable = ({
  requisitions,
  onRequisitionUpdated,
  searchTerm,
  onSearchTermChange,
  currentPage,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  error,
}: RequisitionsTableProps) => {
  const [selectedRequisition, setSelectedRequisition] =
    useState<Requisition | null>(null);
  const [viewingRequisition, setViewingRequisition] =
    useState<Requisition | null>(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issuingRequisition, setIssuingRequisition] = useState<Requisition | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returningRequisition, setReturningRequisition] =
    useState<Requisition | null>(null);
  const { hasPermission } = useAuthStore();
  const canReturn = hasPermission("requisitions.return");

  // A requisition is returnable when at least one item has been issued and there's
  // still un-returned quantity left to send back.
  const isReturnable = (req: Requisition) => {
    if (
      req.status !== RequisitionStatus.Issued &&
      req.status !== RequisitionStatus.PartiallyIssued
    ) {
      return false;
    }
    return (req.items ?? []).some(
      (it) => (it.quantityIssued ?? 0) > (it.quantityReturned ?? 0)
    );
  };

  const displayRequisitions = requisitions;

  return (
    <div className="space-y-4">
      <div className="relative w-64 ml-auto">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search requisitions..."
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Purpose</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Required By</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`loading-${index}`}>
                  <TableCell colSpan={7}>
                    <div className="h-6 animate-pulse rounded bg-muted" />
                  </TableCell>
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-red-600">
                  Failed to load requisitions
                </TableCell>
              </TableRow>
            ) : displayRequisitions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-gray-500">
                  No requisitions found
                </TableCell>
              </TableRow>
            ) : displayRequisitions.map((req) => (
              <TableRow key={req.id}>
                <TableCell className="font-medium">{req.purpose}</TableCell>
                <TableCell className="text-sm">{req.departmentName}</TableCell>
                <TableCell className="text-sm max-w-[260px]">
                  <div className="truncate" title={req.items.map(i => `${getRequisitionItemName(i)} (${getRequisitionItemOwner(i)}) ×${i.quantityRequested}`).join(", ")}>
                    {req.items.map(getRequisitionItemName).join(", ")}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {req.items
                      .map((i) => `Owner: ${getRequisitionItemOwner(i)}`)
                      .join(", ")}
                  </div>
                  <span className="text-muted-foreground text-xs">
                    {req.items.length} item{req.items.length !== 1 ? "s" : ""}
                  </span>
                </TableCell>
                <TableCell className="text-sm">
                  {new Date(req.requiredByDate).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      STATUS_COLORS[req.status] as
                        | "outline"
                        | "secondary"
                        | "default"
                        | "destructive"
                    }
                  >
                    {getStatusLabel(req.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">
                  {new Date(req.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-2 justify-end">
                    {req.status === RequisitionStatus.PendingApproval ? (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            setViewingRequisition(req);
                            setApprovalDialogOpen(true);
                          }}
                          className="gap-1"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Review
                        </Button>
                      </>
                    ) : req.status === RequisitionStatus.Approved ||
                      req.status === RequisitionStatus.PartiallyIssued ? (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            setIssuingRequisition(req);
                            setIssueDialogOpen(true);
                          }}
                          className="gap-1"
                        >
                          <PackageCheck className="w-4 h-4" />
                          Issue Stock
                        </Button>
                        {canReturn && isReturnable(req) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setReturningRequisition(req);
                              setReturnDialogOpen(true);
                            }}
                            className="gap-1"
                          >
                            <Undo2 className="w-4 h-4" />
                            Return
                          </Button>
                        )}
                      </>
                    ) : req.status === RequisitionStatus.Issued && canReturn && isReturnable(req) ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setReturningRequisition(req);
                          setReturnDialogOpen(true);
                        }}
                        className="gap-1"
                      >
                        <Undo2 className="w-4 h-4" />
                        Return
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedRequisition(req);
                          setDetailsDialogOpen(true);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        currentPage={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={Math.max(1, totalPages)}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />

      <RequisitionApprovalDialog
        requisition={viewingRequisition}
        open={approvalDialogOpen}
        onOpenChange={setApprovalDialogOpen}
        onSuccess={() => {
          onRequisitionUpdated?.();
        }}
      />

      <IssueRequisitionDialog
        requisition={issuingRequisition}
        open={issueDialogOpen}
        onOpenChange={setIssueDialogOpen}
        onSuccess={() => {
          onRequisitionUpdated?.();
        }}
      />

      <RequisitionReturnDialog
        requisition={returningRequisition}
        open={returnDialogOpen}
        onOpenChange={setReturnDialogOpen}
        onSuccess={() => {
          onRequisitionUpdated?.();
        }}
      />

      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="grid max-h-[88vh] w-[calc(100vw-1.5rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:w-[92vw] lg:max-w-[900px]">
          <DialogHeader className="border-b px-6 py-5">
            <DialogTitle>Requisition Details</DialogTitle>
            <DialogDescription>
              {selectedRequisition?.requisitionNumber
                ? `${selectedRequisition.requisitionNumber} · ${selectedRequisition.purpose}`
                : selectedRequisition?.purpose}
            </DialogDescription>
          </DialogHeader>

          {selectedRequisition && (
            <div className="min-h-0 overflow-y-auto overflow-x-hidden px-6 py-5">
              <div className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
                    <p className="text-xs text-muted-foreground">Department</p>
                    <p className="text-sm font-medium">{selectedRequisition.departmentName}</p>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge
                      variant={
                        STATUS_COLORS[selectedRequisition.status] as
                          | "outline"
                          | "secondary"
                          | "default"
                          | "destructive"
                      }
                    >
                      {getStatusLabel(selectedRequisition.status)}
                    </Badge>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
                    <p className="text-xs text-muted-foreground">Required By</p>
                    <p className="text-sm font-medium">
                      {new Date(selectedRequisition.requiredByDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="rounded-lg border bg-muted/40 p-3 space-y-1">
                    <p className="text-xs text-muted-foreground">Created By</p>
                    <p className="text-sm font-medium">{selectedRequisition.createdBy}</p>
                  </div>
                </div>

                <div className="rounded-lg border bg-muted/40 px-4 py-3">
                  <p className="text-xs text-muted-foreground mb-1">Purpose</p>
                  <p className="text-sm">{selectedRequisition.purpose}</p>
                </div>

                <div>
                  <p className="text-sm font-medium mb-3">
                    Requisition Items ({selectedRequisition.items.length})
                  </p>
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>Type</TableHead>
                          <TableHead className="min-w-[220px]">Item</TableHead>
                          <TableHead>Reference</TableHead>
                          <TableHead>Owner</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead>Source</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedRequisition.items.map((item, idx) => {
                          const isSeed = isCustomerSeedItem(item);
                          const description = getRequisitionItemDescription(item);
                          return (
                            <TableRow key={item.id ?? idx}>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className={
                                    isSeed
                                      ? "border-amber-300 bg-amber-50 text-amber-700"
                                      : "border-blue-200 bg-blue-50 text-blue-700"
                                  }
                                >
                                  {getRequisitionItemType(item)}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium">
                                {getRequisitionItemName(item)}
                                {description && (
                                  <div className="text-xs font-normal text-muted-foreground">
                                    {description}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {getRequisitionItemReference(item)}
                              </TableCell>
                              <TableCell className="text-sm">
                                {getRequisitionItemOwner(item)}
                              </TableCell>
                              <TableCell className="text-right text-sm">
                                {selectedRequisition.items[idx].quantityRequested}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {item.unitOfMeasure}
                              </TableCell>
                              <TableCell className="text-sm">
                                {getRequisitionItemSource(item)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {selectedRequisition.notes && (
                  <div className="rounded-lg border px-4 py-3">
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{selectedRequisition.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="border-t px-6 py-4">
            <Button variant="secondary" onClick={() => setDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
